import { z } from 'zod';
import { TIPOS_DE_ESTUDIO, TIPOS_DE_ESTUDIO_CON_LADO, } from '../catalogos/componentes.js';
import { esquemaId, esquemaMotivo } from './comunes.js';
import { esquemaInstante } from './cita.js';
/**
 * Ordenes, estudios, resultados y muestras (F3.4, STUDY-01, plan maestro §8.6 y
 * §13.1).
 *
 * Tres cosas DISTINTAS que no se mezclan:
 * - el PERMISO para consultar o escribir (`estudios:leer`, `estudios:escribir`,
 *   `estudios:publicar`);
 * - el ESTADO CLINICO del resultado (preliminar, final, corregido, anulado);
 * - la PUBLICACION al portal (no publicado, publicado, retirado), con su propio
 *   historial. Un resultado final no se publica solo por existir.
 *
 * El resultado va dentro del estudio (uno por estudio): el plan admite embeber
 * piezas si la semantica se conserva (§13.1).
 */
export const PRIORIDADES_DE_ORDEN = ['rutina', 'urgente'];
export const ESTADOS_DE_ORDEN = ['solicitada', 'en-proceso', 'completada', 'cancelada'];
export const ESTADOS_DE_MUESTRA = ['tomada', 'enviada', 'recibida', 'rechazada'];
export const ESTADOS_CLINICOS_DE_RESULTADO = [
    'pendiente',
    'preliminar',
    'final',
    'corregido',
    'anulado',
];
export const ESTADOS_DE_PUBLICACION = ['no-publicado', 'publicado', 'retirado'];
/** Solo lo final (o su correccion) se puede publicar. */
export const PUBLICABLES = ['final', 'corregido'];
export const esquemaCrearOrden = z
    .object({
    pacienteId: esquemaId,
    encuentroId: esquemaId.optional(),
    tipo: z.enum(TIPOS_DE_ESTUDIO),
    indicacion: z.string().trim().min(2, 'Escriba la indicación').max(500),
    prioridad: z.enum(PRIORIDADES_DE_ORDEN).default('rutina'),
})
    .strict();
export const esquemaRegistrarMuestra = z
    .object({
    /** Identificador de la etiqueta del tubo o frasco; unico en el consultorio. */
    identificador: z
        .string()
        .trim()
        .regex(/^[A-Za-z0-9-]{3,40}$/, 'Identificador de 3 a 40 letras, números o guiones'),
    tipoMuestra: z.string().trim().min(2).max(120),
    tomadaEn: esquemaInstante,
    /** De donde se tomo (F6.3). Obligatoria en un procedimiento de gastroenterologia. */
    localizacion: z.string().trim().max(120).default(''),
})
    .strict();
export const esquemaCambiarMuestra = z
    .object({ estado: z.enum(ESTADOS_DE_MUESTRA), motivo: esquemaMotivo.optional() })
    .strict()
    .refine((c) => c.estado !== 'rechazada' || c.motivo !== undefined, {
    message: 'Rechazar una muestra exige motivo',
    path: ['motivo'],
});
export const esquemaRegistrarEstudio = z
    .object({
    pacienteId: esquemaId,
    ordenId: esquemaId.optional(),
    tipo: z.enum(TIPOS_DE_ESTUDIO),
    realizadoEn: esquemaInstante,
    /** Documentos de la paciente ya disponibles (pasaron la cuarentena). */
    archivoIds: z.array(esquemaId).max(20).default([]),
    descripcion: z.string().trim().max(500).default(''),
    /** Ojo del estudio (F6.1). Obligatorio en los de un lado; prohibido en los demas. */
    lateralidad: z.enum(['derecha', 'izquierda']).optional(),
    /**
     * Patologia (F6.3): la muestra a la que pertenece, por el identificador de su
     * etiqueta. Obligatoria en patologia; prohibida en los demas.
     */
    muestraIdentificador: z
        .string()
        .trim()
        .regex(/^[A-Za-z0-9-]{3,40}$/)
        .optional(),
})
    .strict()
    .superRefine((estudio, ctx) => {
    const esPatologia = estudio.tipo === 'patologia';
    if (esPatologia && !estudio.muestraIdentificador) {
        ctx.addIssue({
            code: 'custom',
            message: 'Indique el identificador de la muestra',
            path: ['muestraIdentificador'],
        });
    }
    if (!esPatologia && estudio.muestraIdentificador) {
        ctx.addIssue({
            code: 'custom',
            message: 'Solo un resultado de patología se vincula a una muestra',
            path: ['muestraIdentificador'],
        });
    }
    const conLado = TIPOS_DE_ESTUDIO_CON_LADO.includes(estudio.tipo);
    if (conLado && !estudio.lateralidad) {
        ctx.addIssue({ code: 'custom', message: 'Indique el ojo', path: ['lateralidad'] });
    }
    if (!conLado && estudio.lateralidad) {
        ctx.addIssue({
            code: 'custom',
            message: 'Este estudio no lleva lado',
            path: ['lateralidad'],
        });
    }
});
export const esquemaGuardarResultado = z
    .object({
    versionEsperada: z.number().int().min(0),
    estado: z.enum(['preliminar', 'final', 'corregido', 'anulado']),
    /** La interpretacion del profesional, separada de lo que reporta el estudio. */
    interpretacion: z.string().trim().max(5000).default(''),
    /** Obligatorio al corregir o anular lo que ya era final. */
    motivo: esquemaMotivo.optional(),
})
    .strict();
export const esquemaPublicarResultado = z
    .object({ versionEsperada: z.number().int().min(0) })
    .strict();
export const esquemaRetirarResultado = z
    .object({ versionEsperada: z.number().int().min(0), motivo: esquemaMotivo })
    .strict();
//# sourceMappingURL=estudio.js.map